// App shell, port of src/app/venue/layout.tsx in the Next.js app.
// Applies venue branding via CSS variables that override the platform
// defaults: --venue-accent, --venue-bg, --venue-text, plus the existing
// --accent / --bg fallbacks the SPA stylesheet uses.
//
// Visual structure:
//   fixed inset-0 flex column
//     header  (sticky / non-scrolling)
//     main    (scrollable, flex-1)
//     bottom nav (non-scrolling)
//
// That's what gives the SPA its iOS-app feel, only main scrolls, the
// chrome stays glued.
import { useEffect, useRef } from "react";
import type { ReactNode, CSSProperties } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { useVenue } from "../lib/venue";
import { useAuth } from "../lib/auth";
import { fontStackFor } from "../lib/font";
import { accentForegroundFor } from "../lib/contrast";
import { shapeVars } from "../lib/shape";
import { pageTitle } from "../lib/page-title";
import { awardDailyCheckin } from "../lib/api";

export function VenueAppShell({
  children,
  variant = "app",
}: {
  children: ReactNode;
  // "web" opts out of the fixed app-shell layout (document scroll + website
  // chrome) for record-shop storefronts in a browser. See .venue-app.web CSS.
  variant?: "app" | "web";
}) {
  const { data: venueData } = useVenue();
  const venue = venueData?.venue;
  const { user } = useAuth();
  const qc = useQueryClient();
  // Current route path, drives the per-page tab title (see the <title> effect).
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Daily loyalty check-in. Server is idempotent (dedup via DailyCheckin
  // unique constraint), but we still gate clientside so we don't spam
  // the endpoint on every re-render. Fires once per app mount.
  const checkinFiredFor = useRef<string | null>(null);
  useEffect(() => {
    if (!user || !venue?.id || !venue.loyaltyEnabled) return;
    const key = `${venue.id}:${user.id}`;
    if (checkinFiredFor.current === key) return;
    checkinFiredFor.current = key;
    awardDailyCheckin(venue.id)
      .then((res) => {
        if (res.ok && res.pointsAwarded) {
          // Refresh subscription query so the points pill updates.
          qc.invalidateQueries({ queryKey: ["my-subscription", venue.id] });
        }
      })
      .catch(() => {
        /* silent, fallback to no points on failure */
      });
  }, [user, venue, qc]);

  // Sync the page <title> + meta tags with the venue. Cheap PWA polish
  //, same as Next.js's generateMetadata. Title is route-aware ("<venue> -
  // <page>", e.g. "Madruga Festival - My Account") so each screen is
  // distinguishable in the tab / history; the home route stays just the venue.
  useEffect(() => {
    if (!venue) return;
    document.title = pageTitle(venue.appName || venue.name, pathname);
    const theme = document.querySelector('meta[name="theme-color"]');
    if (theme && venue.backgroundColor) {
      theme.setAttribute("content", venue.backgroundColor);
    }
    if (venue.appDescription) {
      let desc = document.querySelector('meta[name="description"]');
      if (!desc) {
        desc = document.createElement("meta");
        desc.setAttribute("name", "description");
        document.head.appendChild(desc);
      }
      desc.setAttribute("content", venue.appDescription);
    }
  }, [venue, pathname]);

  const bg = venue?.backgroundColor || "#0a0a0a";
  const bgIsLight = isLightColor(bg);

  // Pin the document body + html backgrounds to the venue colour. The router
  // mounts a fresh VenueAppShell per branch (splash vs /book vs /admin etc.),
  // so on route swaps the .venue-app div briefly disappears; if <body> is
  // left on its OS-dependent default (--background: #ffffff for light-mode
  // users), the transition flashes white. Setting body/html here keeps the
  // dark venue colour behind everything so route-swap gaps are invisible.
  // Toka fix 2026-09-14. Runs on every venue-bg change; cleanup restores
  // the CSS default on unmount so non-venue pages (admin console via
  // browser back / bookmark) aren't left with a hardcoded colour.
  useEffect(() => {
    if (!venue) return;
    const prevBody = document.body.style.backgroundColor;
    const prevHtml = document.documentElement.style.backgroundColor;
    document.body.style.backgroundColor = bg;
    document.documentElement.style.backgroundColor = bg;
    return () => {
      document.body.style.backgroundColor = prevBody;
      document.documentElement.style.backgroundColor = prevHtml;
    };
  }, [venue, bg]);
  // ADMIN pages use a NEUTRAL accent, never the venue's: brand accents belong
  // on the public site, in the management UI they ranged from loud (Madruga
  // gold everywhere) to broken (The Certs' near-black accent vs near-black
  // text). Neutral = ink on a light theme, paper on a dark one; guaranteed
  // contrast for every venue and consistent with the monochrome sidebar.
  const isAdmin = pathname === "/admin" || pathname.startsWith("/admin/");
  const accent = isAdmin ? (bgIsLight ? "#161616" : "#ededed") : venue?.accentColor || "#6366f1";
  // Body text ALWAYS contrasts the BACKGROUND (luminance), it must NOT come
  // from `textColor`, which is the on-accent colour (typically white). Using
  // textColor for body text blanked out everything on a light theme.
  const text = bgIsLight ? "#111111" : "#FFFFFF";
  // Text drawn ON the accent colour (primary buttons): an explicit textColor
  // wins (matches the "Text-on-accent colour" admin field + the live preview),
  // else contrast the accent.
  // Contrast-guarded on-accent text colour (see lib/contrast.ts).
  const accentFg = isAdmin
    ? bgIsLight
      ? "#ffffff"
      : "#111111"
    : accentForegroundFor(accent, venue?.textColor);

  // Font: a custom uploaded font (served via @font-face below) wins, else a
  // curated family, else the platform default.
  const customFontUrl = venue?.appConfig?.customFontUrl;
  const fontStack = customFontUrl
    ? `"VenueCustomFont", ${fontStackFor(venue?.fontFamily)}`
    : venue?.fontFamily
      ? fontStackFor(venue.fontFamily)
      : undefined;

  // CSS variables, both the venue-scoped ones AND the platform ones
  // (--accent / --bg) so older selectors keep working.
  const style: CSSProperties = {
    // The venue app is venue-themed, never OS-themed, pin the color scheme
    // so native controls (inputs, scrollbars) match the venue background.
    colorScheme: isLightColor(bg) ? "light" : "dark",
    // venue-scoped
    "--venue-accent": accent,
    "--venue-bg": bg,
    "--venue-text": text,
    // platform fallbacks
    "--accent": accent,
    "--accent-fg": accentFg,
    "--bg": bg,
    // Shape & density vars (radius + content padding).
    ...shapeVars(venue?.appConfig?.shape),
    ...(fontStack ? { "--venue-font": fontStack, fontFamily: fontStack } : {}),
  } as CSSProperties;

  return (
    <div className={variant === "web" ? "venue-app web" : "venue-app"} style={style}>
      {customFontUrl ? (
        <style>{`@font-face{font-family:"VenueCustomFont";src:url("${customFontUrl}");font-display:swap;}`}</style>
      ) : null}
      {children}
      {/* Portal target for modals/dialogs. Direct child of .venue-app so it
          sits above the header/bottom-nav stacking context, escapes the
          scrolling content area, and still inherits the venue theme vars
          (--venue-accent etc.) set on .venue-app. */}
      <div id="modal-root" />
    </div>
  );
}

// Luminance check, matches src/lib/color-utils.ts in the Next.js app.
function isLightColor(hex: string): boolean {
  const m = hex.replace(/^#/, "");
  if (m.length !== 6) return false;
  const r = parseInt(m.slice(0, 2), 16);
  const g = parseInt(m.slice(2, 4), 16);
  const b = parseInt(m.slice(4, 6), 16);
  // Standard luminance formula.
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.5;
}
